/**
 * Endpoints de **Gestión → Administración**: boletaje masivo y generador masivo
 * de boletas.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CatalogosBoletasRespuesta,
  NotasBoletajeRespuesta,
  PeticionBoletaje,
  PeticionGenerador,
  PrevisualizacionRespuesta,
  ResultadoBoletas,
} from './administracion-boletas.types';

const CLAVE = 'gestion';

/** Convierte los filtros en cadena de consulta. */
function cadenaDe(filtros: { [clave: string]: string | number | undefined }): string {
  const parametros = new URLSearchParams();

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor === undefined || valor === null || valor === '') continue;

    parametros.set(clave, String(valor));
  }

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const boletasKeys = {
  catalogos: () => [CLAVE, 'administracion', 'boletas', 'catalogos'] as const,
  notas: (desde: string, hasta: string, producto: string) =>
    [CLAVE, 'administracion', 'boletas', 'notas', desde, hasta, producto] as const,
};

export const boletasApi = {
  catalogos: (signal?: AbortSignal): Promise<CatalogosBoletasRespuesta> =>
    apiClient.get<CatalogosBoletasRespuesta>(
      '/api/gestion/administracion/boletas/catalogos',
      { signal },
    ),

  notas: (
    desde: string,
    hasta: string,
    producto: string,
    signal?: AbortSignal,
  ): Promise<NotasBoletajeRespuesta> =>
    apiClient.get<NotasBoletajeRespuesta>(
      `/api/gestion/administracion/boletas/notas${cadenaDe({
        desde,
        hasta,
        producto_id: producto,
      })}`,
      { signal },
    ),

  boletaje: (datos: PeticionBoletaje): Promise<ResultadoBoletas> =>
    apiClient.post<ResultadoBoletas>('/api/gestion/administracion/boletas/boletaje', {
      body: datos,
    }),

  previsualizar: (datos: PeticionGenerador): Promise<PrevisualizacionRespuesta> =>
    apiClient.post<PrevisualizacionRespuesta>(
      '/api/gestion/administracion/boletas/generador/previsualizar',
      { body: datos },
    ),

  generador: (datos: PeticionGenerador): Promise<ResultadoBoletas> =>
    apiClient.post<ResultadoBoletas>('/api/gestion/administracion/boletas/generador', {
      body: datos,
    }),
};

/** Catálogos de ambos módulos (series, productos, empresa y correlativo). */
export function useCatalogosBoletas() {
  return useQuery({
    queryKey: boletasKeys.catalogos(),
    queryFn: ({ signal }) => boletasApi.catalogos(signal),
    staleTime: 60_000,
  });
}

/** Notas de venta por boletiar del rango. */
export function useNotasBoletaje(desde: string, hasta: string, producto: string, activo: boolean) {
  return useQuery({
    queryKey: boletasKeys.notas(desde, hasta, producto),
    queryFn: ({ signal }) => boletasApi.notas(desde, hasta, producto, signal),
    enabled: activo,
    retry: false,
  });
}

/** Ejecuta el boletaje masivo. */
export function useBoletaje() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: PeticionBoletaje) => boletasApi.boletaje(datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'administracion'] });
    },
  });
}

/** Previsualiza el generador masivo (sin escribir). */
export function usePrevisualizarGenerador() {
  return useMutation({
    mutationFn: (datos: PeticionGenerador) => boletasApi.previsualizar(datos),
  });
}

/** Ejecuta el generador masivo. */
export function useGeneradorMasivo() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: PeticionGenerador) => boletasApi.generador(datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'administracion'] });
    },
  });
}
